/**
 * Legal page content, rendered through the shared policy template.
 *
 * The privacy copy is the client's own document ("Privacy Policy copy Save on
 * Broadband.docx", supplied 2026-08-31), reproduced verbatim and split into the
 * eleven sections its own headings define. Its numbering matters: the cookie
 * section cross-references "the website mentioned in 8.1 above", which only
 * resolves while Cookies stays section 08.
 *
 * Contact details are interpolated from CONTACT_DETAILS rather than retyped, so
 * the policy cannot drift from the rest of the site.
 *
 * Two transcription defects in the source were corrected on the client's word
 * (2026-08-31), and nothing else:
 *   - "client flo latch Media" is **Client Flow and Latech Media**, two named
 *     lead suppliers.
 *   - The postal address reads "Chuch Row Chambers" in section 07 and "Church
 *     Row Chambers" in section 11; the correct spelling is used in both.
 *
 * The mixed use of "Neatley" and "SaveonBroadband" is NOT an error and must not
 * be normalised: Neatley Ltd is the business, SaveonBroadband is the product it
 * runs. Where the document says Neatley processes an order it means the
 * company; where it says SaveonBroadband it means the site.
 */
import { CONTACT_DETAILS } from './siteContent.js';

export const PRIVACY_POLICY = {
  slug: 'privacy',
  heroTitle: 'Privacy Policy',
  heroSubtitle: 'Learn more about how we keep your data protected.',
  eyebrow: 'Privacy Policy',
  title: 'Keeping Your Data Safe.',
  intro:
    'This Privacy Policy will explain how SaveonBroadband safeguards your information and how we use it to improve our products and services for the benefit of all our customers and website users.',
  sections: [
    {
      title: 'Introduction & Scope',
      blocks: [
        'SaveonBroadband acts as the Data Controller for the purposes of UK data protection law.',
        'We process personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. The lawful bases we rely on are set out below. The nature of our business means that, on occasion, we’ll require some personal details and some business details from our customers and website users.',
        'The information you share with us, along with data on how you use our websites helps us to better understand our customers as a group and as individuals. This in turn helps us to provide a range of products and services that meet your needs.',
      ],
    },
    {
      title: 'What data do we collect?',
      blocks: [
        'SaveonBroadband collects the following data:',
        [
          'Personal identification information (name, email address, contact telephone number, address including post code)',
          'Business information (company name, job title, number of employees, company email address and company contact telephone number)',
        ],
      ],
    },
    {
      title: 'How do we collect your data?',
      blocks: [
        'You directly provide SaveonBroadband with most of the data we collect. We process personal data where it is necessary to perform a contract with you, or to take steps at your request prior to entering into a contract. We collect and process data when you:',
        [
          'Register online or place an order for any of our products or services',
          'Voluntarily complete a customer survey or provide feedback on any of our message boards or via email',
          'Use or view our website via your browser’s cookies',
          'Process orders for products and services',
          'Provide customer support',
        ],
        'You may also indirectly provide SaveonBroadband with data when you:',
        ['Opt-in to services from any third-party websites, such as Client Flow and Latech Media'],
      ],
    },
    {
      title: 'How will we use your data?',
      blocks: [
        'SaveonBroadband collects your data so that we can contact you via telephone, email and SMS to:',
        [
          'Process your order and manage your account',
          'Contact you with special offers on other products and services we think may be of interest',
        ],
        'If you agree, SaveonBroadband will share your data with our partner companies including Virgin Media/O2, BT Mainline',
        'If you agree, Neatley may share your data with third party companies so that they may offer you their products and services. The third parties we work with include O2.',
        'When Neatley processes your order, it may send your data to, and also use the resulting information from, credit reference agencies to prevent fraudulent purchases.',
        'When Neatley processes your order, it may also send your data to other service providers, for example, payment processors, distributors/agents of our products, suppliers who develop or host our sites.',
        'We may process personal data where it is necessary for our legitimate business interests, provided those interests are not overridden by your rights and freedoms.',
        'These interests include:',
        [
          'Operating and improving our products and services',
          'Managing and administering our business operations',
          'Preventing fraud and ensuring network and information security',
          'Conducting analytics and customer insight to improve our services',
          'Conducting telephone-based marketing activities in line with applicable laws',
        ],
        'Where we rely on legitimate interests, we ensure that appropriate safeguards are in place to protect your rights.',
        'We rely on your consent to process personal data in certain circumstances, particularly in relation to electronic marketing communications (including email and SMS), in accordance with the Privacy and Electronic Communications Regulations (PECR).',
        'Where consent is required:',
        [
          'We will obtain your clear and informed consent before sending marketing communications',
          'You have the right to withdraw your consent at any time',
          'All marketing communications will include a clear opt-out mechanism',
        ],
        'Legitimate Interests (Soft Opt-In) – Marketing where permitted under PECR, we may send marketing communications about similar products and services to existing customers using the “soft opt-in” rule, where:',
        [
          'You have previously purchased or shown interest in our products or services',
          'You were given the opportunity to opt out at the time your data was collected',
          'You are given the opportunity to opt out in every subsequent communication',
        ],
        'We may process your personal data where necessary to comply with legal and regulatory obligations, including:',
        [
          'Fraud prevention and detection',
          'Compliance with financial, regulatory, or legal requirements',
          'Responding to lawful requests from public authorities and regulators',
        ],
      ],
    },
    {
      title: 'How do we store your data?',
      blocks: [
        'SaveonBroadband have put in place appropriate security measures and use encryption through technology to prevent your personal data from being accidentally lost, used or accessed in an unauthorised way, altered or disclosed.',
        'SaveonBroadband limits access to your personal data to those employees and other staff who have a business need to have such access. All such people are subject to a contractual duty of confidentiality.',
        'SaveonBroadband have put in place procedures to deal with any actual or suspected personal data breach. In the event of any such breach, we have systems in place to work with applicable regulators. In addition, in certain circumstances (e.g. where we are legally required to do so) we may notify you of this breach.',
        'SaveonBroadband will retain your personal data for no longer than necessary to fulfil the purposes it was collected for, including for the purposes of satisfying any legal, regulatory, or reporting requirements. Unless otherwise required, we will delete your data 3 years after your last interaction with us. If you wish us to stop processing your data for marketing, you can opt-out of marketing at any time.',
        'Data is stored securely and will be deleted permanently after a period of 3 years. Data will be permanently destroyed using AWS data deletion tools.',
      ],
    },
    {
      title: 'Marketing',
      blocks: [
        'SaveonBroadband would like to send you information about products and services we think may be of interest, as well as those of our partner companies. If you have agreed to receive marketing, you may opt out at any time.',
        `You have the right at any time to stop SaveonBroadband from contacting you for marketing purposes or giving your data to partner companies. If you no longer wish to be contacted for marketing purposes, please email: ${CONTACT_DETAILS.privacyEmail}`,
      ],
    },
    {
      title: 'What are your data protection rights?',
      blocks: [
        'SaveonBroadband would like to make sure you are fully aware of all your data protection rights. Every user is entitled to the following:',
        [
          'The right to access – you have the right to request copies of your personal data from SaveonBroadband. We will not charge a fee for your request unless it is clearly unfounded, repetitive, or excessive',
          'The right to rectification – You have the right to request that SaveonBroadband correct any information you believe is inaccurate. You also have the right to request SaveonBroadband to complete the information you believe is incomplete.',
          'The right to erasure – You have the right to request that SaveonBroadband erase your personal data, under certain conditions.',
          'The right to restrict processing – You have the right to request that SaveonBroadband restrict the processing of your personal data, under certain conditions.',
          'The right to object to processing – You have the right to object to SaveonBroadband processing of your personal data, under certain conditions.',
          'The right to data portability – You have the right to request that SaveonBroadband transfer the data that we have collected to another organisation, or directly to you, under certain conditions.',
        ],
        'All personal data processed by SaveonBroadband is stored and processed within the United Kingdom. We do not transfer personal data outside of the UK.',
        'SaveonBroadband will respond within one month to any request made. If you would like to exercise any of the above rights, please contact us via one of the following methods.',
        `By email at: ${CONTACT_DETAILS.privacyEmail}`,
        'Write to us at:\nSave on Broadband from Neatley LTD\n12 Church Row Chambers\nPreston\nPR4 5PN',
        `Our Data Protection Officer is ${CONTACT_DETAILS.dataProtectionOfficer}`,
      ],
    },
    {
      title: 'Cookies',
      blocks: [
        'Cookies are text files placed on your computer to collect standard Internet log information and visitor behaviour information. When you visit our websites, we may collect information from you automatically through cookies or similar technology. For general information about cookies, visit www.allaboutcookies.org.',
        'SaveonBroadband uses cookies in a range of ways to improve your experience on our website, including:',
        ['Keeping you signed in', 'Understanding how you use our website'],
        'There are a number of different types of cookies, however, our website uses:',
        [
          'Functionality – SaveonBroadband uses these cookies so that we recognise you on our website and remember your previously selected preferences. These could include what language you prefer and location you are in. A mix of first-party and third-party cookies are used.',
          'Advertising – SaveonBroadband uses these cookies to collect information about your visit to our website, the content you viewed, the links you followed and information about your browser, device and your IP address. SaveonBroadband sometimes shares limited aspects of this data with other companies.',
        ],
        'You can set your browser not to accept cookies, and the website mentioned in 8.1 above tells you how to remove cookies from your browser. However, in a few cases, some of our website features may not function as a result.',
      ],
    },
    {
      title: 'Other websites',
      blocks: [
        'The SaveonBroadband website contains links to other websites. Our Privacy Policy applies only to our website, so if you click on a link to another website, you should read their Privacy Policy.',
      ],
    },
    {
      title: 'Amendments',
      blocks: [
        'SaveonBroadband keeps its privacy policy under regular review and places any updates on this web page.',
      ],
    },
    {
      title: 'Contact information',
      blocks: [
        'If you have any questions about SaveonBroadband privacy policy, the data we hold on you, or you would like to exercise one of your data protection rights, please do not hesitate to contact us using the contact information below.',
        `Email us at: ${CONTACT_DETAILS.privacyEmail}\nCall us on: ${CONTACT_DETAILS.phone}\nWrite to us at: ${CONTACT_DETAILS.address}`,
        'Should you wish to report a complaint, SaveonBroadband would like the opportunity to put things right for you in the first instance. However, if you feel that SaveonBroadband has not addressed your concern in a satisfactory manner, you may contact the Information Commissioner’s Office on the details below.',
        'Information Commissioner’s Office\nWycliffe House\nWater Lane\nWilmslow\nCheshire\nSK9 5AF\n0303 123 1113',
      ],
    },
  ],
};

/**
 * Ported from section 08 of the client's privacy policy on 2026-08-31 — their
 * own cookie wording, rather than the drafted text that stood here before.
 *
 * Two adaptations were unavoidable. The source says "the website mentioned in
 * 8.1 above", a cross-reference that has no referent on this page, so
 * allaboutcookies.org is named directly. And the source is silent on how to
 * withdraw consent on this site, which PECR requires us to tell people, so the
 * sentence about the on-page consent control is kept — it describes a control
 * this build actually ships.
 *
 * ⚠️ STILL NOT LAUNCH-READY, hence `pending`. This says what categories of
 * cookie are set and why, but names no individual cookie, no duration and no
 * third party. A compliant cookie policy needs that inventory, and it has to
 * come from whoever owns compliance.
 */
export const COOKIES_POLICY = {
  slug: 'cookies',
  heroTitle: 'Cookie Policy',
  heroSubtitle: 'Learn more about the cookies we use and why.',
  eyebrow: 'Cookie Policy',
  title: 'How We Use Cookies.',
  intro:
    'This policy explains what cookies SaveonBroadband sets, what they are used for, and how you can control them.',
  pending: true,
  sections: [
    {
      title: 'What cookies are',
      blocks: [
        'Cookies are text files placed on your computer to collect standard Internet log information and visitor behaviour information. When you visit our websites, we may collect information from you automatically through cookies or similar technology. For general information about cookies, visit www.allaboutcookies.org.',
      ],
    },
    {
      title: 'How we use cookies',
      blocks: [
        'SaveonBroadband uses cookies in a range of ways to improve your experience on our website, including:',
        ['Keeping you signed in', 'Understanding how you use our website'],
      ],
    },
    {
      title: 'The types of cookies we use',
      blocks: [
        'There are a number of different types of cookies, however, our website uses:',
        [
          'Functionality – SaveonBroadband uses these cookies so that we recognise you on our website and remember your previously selected preferences. These could include what language you prefer and location you are in. A mix of first-party and third-party cookies are used.',
          'Advertising – SaveonBroadband uses these cookies to collect information about your visit to our website, the content you viewed, the links you followed and information about your browser, device and your IP address. SaveonBroadband sometimes shares limited aspects of this data with other companies.',
        ],
      ],
    },
    {
      title: 'Managing your preferences',
      blocks: [
        'You can change or withdraw your consent at any time using the cookie preferences control on this site.',
        'You can also set your browser not to accept cookies, and www.allaboutcookies.org tells you how to remove cookies from your browser. However, in a few cases, some of our website features may not function as a result.',
      ],
    },
    {
      title: 'Contact us',
      blocks: [
        'If you have any questions about how we use cookies, please contact us at:',
        `Email: ${CONTACT_DETAILS.privacyEmail}\nPhone: ${CONTACT_DETAILS.phone}\nAddress: ${CONTACT_DETAILS.address}`,
      ],
    },
  ],
};

export const POLICIES = {
  privacy: PRIVACY_POLICY,
  cookies: COOKIES_POLICY,
};
